import { Box, Card, CardContent, GridLegacy, Typography, Button, Divider } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import NotificationsIcon from '@mui/icons-material/Notifications';
import GetAppIcon from '@mui/icons-material/GetApp';
import PersonIcon from '@mui/icons-material/Person';
import { Helmet } from 'react-helmet-async';
import { getPageTitle } from 'src/config/projectConfig';
import useAuth from 'src/hooks/useAuth';
import { SxProps } from '@mui/system';

/* ─── Alert header bar component ─── */
export const AlertHeader = ({
  label,
  icon,
  bgColor,
  sx,
}: {
  label: string;
  icon: React.ReactNode;
  bgColor: string;
  sx?: SxProps;
}) => (
  <Box
    display="flex"
    alignItems="center"
    gap={1}
    sx={{
      ...sx,
      bgcolor: bgColor,
      color: '#fff',
      borderRadius: '10px',
      px: 2,
      py: 1,
    }}
  >
    {icon}
    <Typography variant="subtitle2" fontWeight={600} color="inherit">
      {label}
    </Typography>
  </Box>
);

const systemAlerts = [
  "Database 'Cassandra' reports slow query times. Review server load.",
  "User 'Bond.Forgen' account disabled after multiple failed password resets.",
];

const notifications = [
  "Server 'Theta' is experiencing high latency. Check network connections.",
  "User 'Anya.Forger' has been locked out due to too many invalid login attempts.",
];

const recentActivity = [
  { id: 'ra1', text: 'New case assigned to agent Anya Forger', date: '15 Mar 2026 - 14:30' },
  { id: 'ra2', text: "Case status updated to 'In Progress' by agent Loid Forger", date: '14 Mar 2026 - 09:15' },
  { id: 'ra3', text: 'New comment added to case by client Damian Desmond', date: '13 Mar 2026 - 16:45' },
  { id: 'ra4', text: "Case reassigned to team 'Alpha' by manager Sylvia Sherwood", date: '13 Mar 2026 - 10:00' },
  { id: 'ra5', text: 'New case created by client Yor Briar', date: '12 Mar 2026 - 18:20' },
  { id: 'ra6', text: 'New case assigned to agent Anya Forger', date: '15 Mar 2026 - 14:30' },
  { id: 'ra7', text: "Case status updated to 'In Progress' by agent Loid Forger", date: '14 Mar 2026 - 09:15' },
  { id: 'ra8', text: 'New comment added to case by client Damian Desmond', date: '13 Mar 2026 - 16:45' },
  { id: 'ra9', text: "Case reassigned to team 'Alpha' by manager Sylvia Sherwood", date: '13 Mar 2026 - 10:00' },
  { id: 'ra10', text: 'New case created by client Yor Briar', date: '12 Mar 2026 - 18:20' },
];

const customStyle = {
  bannerStyles: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: 0,
    textTransform: 'capitalize',

    mb: 3,
    top: '162px',
    left: '219px',
    borderRadius: '10px',
    transform: 'rotate(0deg)',
    opacity: 1,
  },
  cardStyles: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: 0,
    textTransform: 'capitalize',

    borderRadius: '10px',
    padding: '5px',
    transform: 'rotate(0deg)',
    opacity: 1,
  },
  cardHeader: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: 0,
    textTransform: 'capitalize',
    m: 1.5,
  },

};

const MindtimeDashboard = () => {
  const auth = useAuth();
  const userName = auth?.user?.name || 'John Doe';

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Home')}</title>
      </Helmet>

      <Box p={3}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          Welcome {userName}
        </Typography>

        <GridLegacy container spacing={3}>
          {/* ─── Left column ─── */}
          <GridLegacy item xs={12} md={6}>
            {/* System Alerts */}
            <Card sx={customStyle.bannerStyles}>
              <AlertHeader
                label="System Alerts"
                icon={<WarningIcon sx={{ fontSize: 18 }} />}
                bgColor="#F56565"
              />
              <CardContent>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {systemAlerts.map((alert) => (
                    <Box
                      key={alert}
                      component="li"
                      sx={{ mb: 0.5, fontSize: 13, color: '#4A5568' }}
                    >
                      <Typography variant="body2" fontSize={13}>
                        {alert}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card sx={customStyle.cardStyles}>
              <Typography variant="h6" sx={customStyle.cardHeader} >
                Recent Activity
              </Typography>
              <Divider sx={{ m: 1.5 }} />
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                {recentActivity.map((item) => (
                  <Box
                    key={item.id}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    px={2.5}
                    py={1.25}
                    borderBottom="1px solid #EDF2F7"
                  >
                    <Typography variant="body2" fontSize={13} color="textPrimary">
                      {item.text}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5} whiteSpace="nowrap" ml={2}>
                      <Typography variant="caption" fontSize={12} color="textSecondary">
                        {item.date}
                      </Typography>
                      <PersonIcon sx={{ fontSize: 14, color: '#A0AEC0' }} />
                      <Typography variant="caption" fontSize={12} color="textSecondary">
                        System
                      </Typography>
                    </Box>
                  </Box>
                ))}

                <Box display="flex" justifyContent="flex-end" px={2.5} py={1.5}>
                  <Typography
                    variant="body2"
                    color="primary"
                    fontSize={13}
                    sx={{ cursor: 'pointer', fontWeight: 500 }}
                  >
                    See all
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </GridLegacy>

          {/* ─── Right column ─── */}
          <GridLegacy item xs={12} md={6}>
            {/* Notifications */}
            <Card sx={customStyle.bannerStyles}>
              <AlertHeader
                label="Notifications"
                icon={<NotificationsIcon sx={{ fontSize: 18 }} />}
                bgColor="#1BB4EB"
              />
              <CardContent>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {notifications.map((note) => (
                    <Box
                      key={note}
                      component="li"
                      sx={{ mb: 0.5, fontSize: 13, color: '#4A5568' }}
                    >
                      <Typography variant="body2" fontSize={13}>
                        {note}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* Infographic section */}

            <Card sx={customStyle.cardStyles}>
              <Box display="flex" justifyContent="space-between" alignItems="center" >
                <Typography variant="h6" sx={customStyle.cardHeader} >
                  Infographic View Of Some Sort
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<GetAppIcon />}
                  sx={{
                    bgcolor: '#F56565',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: 12,
                    '&:hover': { bgcolor: '#E53E3E' },
                  }}
                >
                  Export
                </Button>
              </Box>
              <Divider />

              <CardContent>
                {/* Doughnut chart placeholder */}
                <Box
                  height={140}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="#FAFAFA"
                  borderRadius={1}
                  mb={2}
                >
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      borderRadius: '50%',
                      border: '20px solid #F56565',
                      borderTopColor: '#ED8936',
                      borderRightColor: '#ECC94B',
                      borderBottomColor: '#FC8181',
                    }}
                  />
                </Box>

                {/* Bar chart placeholder */}
                <Box
                  height={200}
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="space-around"
                  bgcolor="#FAFAFA"
                  borderRadius={1}
                  px={2}
                  pt={2}
                  pb={1}
                >
                  {[
                    { name: 'Sprint 1', heightBase: 40 },
                    { name: 'Sprint 2', heightBase: 55 },
                    { name: 'Sprint 3', heightBase: 70 },
                    { name: 'Sprint 4', heightBase: 85 },
                    { name: 'Sprint 5', heightBase: 100 },
                  ].map((sprint) => (
                    <Box key={sprint.name} display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                      <Box display="flex" gap={0.25} alignItems="flex-end">
                        {[
                          { ratio: 0.5, color: '#F56565', id: 'bar-a', offset: 0 },
                          { ratio: 0.7, color: '#ED8936', id: 'bar-b', offset: 20 },
                          { ratio: 0.4, color: '#ECC94B', id: 'bar-c', offset: 40 },
                          { ratio: 0.6, color: '#FC8181', id: 'bar-d', offset: 60 },
                        ].map((bar) => (
                          <Box
                            key={`${sprint.name}-${bar.id}`}
                            sx={{
                              width: 10,
                              height: `${(sprint.heightBase + bar.offset) * bar.ratio}px`,
                              bgcolor: bar.color,
                              borderRadius: '2px 2px 0 0',
                            }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" fontSize={10} color="textSecondary">
                        {sprint.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </GridLegacy>
        </GridLegacy>
      </Box>
    </>
  );
};

export default MindtimeDashboard;
